/**
 * Segment CDP Configuration Page - S001
 * Allows org admins to configure Segment write key and tracking options
 */

import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/apiClient';

interface SegmentConfig {
  organization_id: string;
  enabled: boolean;
  track_pageviews: boolean;
  track_actions: boolean;
  track_alerts: boolean;
  configured: boolean;
  created_at: string;
  updated_at: string;
}

export function SegmentIntegration() {
  const [config, setConfig] = useState<SegmentConfig | null>(null);
  const [writeKey, setWriteKey] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [trackPageviews, setTrackPageviews] = useState(true);
  const [trackActions, setTrackActions] = useState(true);
  const [trackAlerts, setTrackAlerts] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load existing configuration
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await apiClient.get('/api/v1/integrations/segment');
      setConfig(response.data);
      setEnabled(response.data.enabled);
      setTrackPageviews(response.data.track_pageviews);
      setTrackActions(response.data.track_actions);
      setTrackAlerts(response.data.track_alerts);
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Failed to load Segment config:', err);
      }
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await apiClient.post('/api/v1/integrations/segment', {
        write_key: writeKey,
        enabled,
        track_pageviews: trackPageviews,
        track_actions: trackActions,
        track_alerts: trackAlerts,
      });

      setSuccess('Segment configuration saved successfully!');
      setWriteKey(''); // Clear key after saving
      await loadConfig();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    setLoading(true);
    setError(null);

    try {
      await apiClient.patch(`/api/v1/integrations/segment/toggle?enabled=${!enabled}`);
      setSuccess(`Segment tracking ${!enabled ? 'enabled' : 'disabled'}`);
      await loadConfig();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to toggle tracking');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to remove Segment integration?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await apiClient.delete('/api/v1/integrations/segment');
      setSuccess('Segment integration removed');
      setConfig(null);
      setWriteKey('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove integration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-black dark:text-white">
          Segment CDP Configuration
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Configure your Segment write key to track user events and analytics.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded">
          {success}
        </div>
      )}

      {config && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-blue-900">Segment is configured</p>
              <p className="text-sm text-blue-700">
                Status: {config.enabled ? 'Active' : 'Inactive'} | 
                Last updated: {new Date(config.updated_at).toLocaleDateString()}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleToggle}
                disabled={loading}
                className={`px-4 py-2 rounded ${
                  config.enabled
                    ? 'bg-yellow-500 hover:bg-yellow-600'
                    : 'bg-green-500 hover:bg-green-600'
                } text-white disabled:opacity-50`}
              >
                {config.enabled ? 'Disable' : 'Enable'}
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded disabled:opacity-50"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Segment Write Key
            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            value={writeKey}
            onChange={(e) => setWriteKey(e.target.value)}
            placeholder="Enter your Segment write key"
            className="w-full rounded border border-stroke bg-gray py-3 px-4.5 text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary"
            required={!config}
          />
          <p className="text-xs text-gray-500 mt-1">
            Find this in your Segment workspace under Settings → API Keys
          </p>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium text-black dark:text-white">
            Tracking Options
          </h4>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-5 w-5 rounded border-gray-300"
            />
            <div>
              <p className="font-medium text-sm">Enable Tracking</p>
              <p className="text-xs text-gray-600">Master toggle for all Segment tracking</p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={trackPageviews}
              onChange={(e) => setTrackPageviews(e.target.checked)}
              className="h-5 w-5 rounded border-gray-300"
            />
            <div>
              <p className="font-medium text-sm">Track Pageviews</p>
              <p className="text-xs text-gray-600">Record page navigation events</p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={trackActions}
              onChange={(e) => setTrackActions(e.target.checked)}
              className="h-5 w-5 rounded border-gray-300"
            />
            <div>
              <p className="font-medium text-sm">Track User Actions</p>
              <p className="text-xs text-gray-600">Record button clicks and user interactions</p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={trackAlerts}
              onChange={(e) => setTrackAlerts(e.target.checked)}
              className="h-5 w-5 rounded border-gray-300"
            />
            <div>
              <p className="font-medium text-sm">Track Alerts</p>
              <p className="text-xs text-gray-600">Record alert creation and resolution events</p>
            </div>
          </label>
        </div>

        <button
          type="submit"
          disabled={loading || (!writeKey && !config)}
          className="w-full rounded bg-primary py-3 px-6 font-medium text-gray hover:bg-opacity-90 disabled:opacity-50"
        >
          {loading ? 'Saving...' : config ? 'Update Configuration' : 'Save Configuration'}
        </button>
      </form>

      <div className="mt-6 p-4 bg-gray-50 dark:bg-meta-4 rounded">
        <h4 className="font-medium text-sm mb-2">How it works:</h4>
        <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
          <li>Your Segment write key is encrypted and stored securely</li>
          <li>Analytics.js is automatically injected when enabled</li>
          <li>Events are sent to your Segment workspace in real-time</li>
          <li>You can disable tracking at any time without removing the key</li>
        </ul>
      </div>
    </div>
  );
}
